import React, { useState, useEffect } from 'react';
import { X, Save, MapPin } from 'lucide-react';

const MapElementForm = ({
  isOpen,
  onClose,
  onSave,
  elementType,
  coordinates,
  elementData
}) => {
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    commune: 'Comuna Simón Bolívar',
    area: '',
    capacity: '',
    contact_person: '',
    description: '',
    status: 'active'
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Pre-fill form if data is provided or reset to blank
      setFormData({
        name: elementData?.name || '',
        contact_email: elementData?.contact_email || elementData?.email || '',
        contact_phone: elementData?.contact_phone || elementData?.phone || '',
        commune: elementData?.commune || elementData?.address || 'Comuna Simón Bolívar',
        area: elementData?.area || elementData?.total_area || '',
        capacity: elementData?.capacity || elementData?.storage_capacity || '',
        contact_person: elementData?.contact_person || '',
        description: elementData?.description || elementData?.notes || '',
        status: elementData?.status || 'active'
      });
    }
  }, [isOpen, elementData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const typeMap = {
        'marker': 'producer', // Default fallback
        'producer': 'producer',
        'crop': 'crop',
        'center': 'center',
        'acopio': 'center',
        'logistics': 'logistics',
        'polygon': 'crop',
        'line': 'logistics',
        'circle': 'crop'
      };

      const type = typeMap[elementType] || 'producer';

      await onSave({
        ...formData,
        type,
        lat: coordinates?.lat || 10.4806,
        lng: coordinates?.lng || -66.9036
      });
    } catch (err) {
      console.error('Error in element form save:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFormTitle = () => {
    switch (elementType) {
      case 'producer':
      case 'Productor':
        return 'Registrar Productor Georreferenciado';
      case 'crop':
      case 'Cultivo':
      case 'polygon':
      case 'circle':
        return 'Registrar Parcela de Cultivo';
      case 'center':
      case 'Centro de Acopio':
      case 'acopio':
        return 'Registrar Centro de Acopio';
      case 'logistics':
      case 'line':
        return 'Registrar Ruta Logística';
      default:
        return 'Registrar Nuevo Elemento Geográfico';
    }
  };

  const isCrop = elementType === 'crop' || elementType === 'Cultivo' || elementType === 'polygon' || elementType === 'circle';
  const isProducer = elementType === 'producer' || elementType === 'Productor' || elementType === 'marker';
  const isCenter = elementType === 'center' || elementType === 'Centro de Acopio' || elementType === 'acopio';
  const isRoute = elementType === 'logistics' || elementType === 'line';

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 flex flex-col my-8">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gray-50">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{getFormTitle()}</h3>
            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5 text-green-600" />
              <span>Lat: {coordinates?.lat?.toFixed(6)}, Lng: {coordinates?.lng?.toFixed(6)}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 flex-1 overflow-y-auto max-h-[70vh]">
          {/* Common Field: Name */}
          <div className="space-y-1">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              {isProducer ? 'Nombre del Productor' : isCenter ? 'Nombre del Centro de Acopio' : isRoute ? 'Nombre de la Ruta' : 'Nombre del Cultivo / Variedad'} *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder={isProducer ? "Ej. Juan Pérez" : isCenter ? "Ej. Silos de Bolívar" : isRoute ? "Ej. Ruta de Distribución Norte" : "Ej. Maíz Amarillo B-350"}
            />
          </div>

          {/* Producer Specific Fields */}
          {isProducer && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Correo de Contacto
                </label>
                <input
                  type="email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="juan@ejemplo.com"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Teléfono
                </label>
                <input
                  type="tel"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="+58 412..."
                />
              </div>
            </div>
          )}

          {/* Crop or Producer Field: Area / Commune */}
          {(isCrop || isProducer || isCenter) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(isCrop || isProducer) && (
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Superficie (Hectáreas) *
                  </label>
                  <input
                    type="number"
                    name="area"
                    required
                    step="0.1"
                    min="0.1"
                    value={formData.area}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="Ej. 12.5"
                  />
                </div>
              )}

              {isCenter && (
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Capacidad de Almacenamiento (Toneladas) *
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    required
                    step="1"
                    min="1"
                    value={formData.capacity}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="Ej. 500"
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Ubicación / Comuna
                </label>
                <select
                  name="commune"
                  value={formData.commune}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="Comuna Simón Bolívar">Comuna Simón Bolívar</option>
                  <option value="Comuna Lanceros del Llano">Comuna Lanceros del Llano</option>
                  <option value="Comuna Sol de Venezuela">Comuna Sol de Venezuela</option>
                  <option value="Comuna Tierra Libre">Comuna Tierra Libre</option>
                </select>
              </div>
            </div>
          )}

          {/* Center Specific Fields */}
          {isCenter && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Persona de Contacto
                </label>
                <input
                  type="text"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="Ej. Ing. Carlos Ruiz"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Teléfono de Contacto
                </label>
                <input
                  type="tel"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="Ej. 0414..."
                />
              </div>
            </div>
          )}

          {/* Description / Notes */}
          <div className="space-y-1">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Descripción / Observaciones
            </label>
            <textarea
              name="description"
              rows="3"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder="Detalles adicionales, observaciones del suelo o tipo de cultivo..."
            />
          </div>

          {/* Status */}
          <div className="space-y-1">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Estado
            </label>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="active"
                  checked={formData.status === 'active' || formData.status === 'Activo'}
                  onChange={handleChange}
                  className="w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Activo / Operacional</span>
              </label>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="inactive"
                  checked={formData.status === 'inactive' || formData.status === 'Inactivo'}
                  onChange={handleChange}
                  className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Inactivo / En Espera</span>
              </label>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="pt-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 -mx-6 -mb-6 p-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors shadow-md"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Guardando...' : 'Guardar en Sistema'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MapElementForm;
